import { JSX, useContext, useEffect, useMemo, useState } from "react";

import { PageSection, SearchBox } from "@app/shared";
import { QESAlert } from "@app/shared/qes-standards";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { BulkProviderChangeProjectTableData } from "@app/shared/provider-change-wizards/interfaces";
import { BulkTargetingWizardContext } from "./TargetingWizardContext";
import { BulkTargetingWizardStepProps } from "./interfaces";

const ProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const BulkTargetingWizardSelectProjects = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);

  const [projectSearchString, setProjectSearchString] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // fetch data from api
    setIsLoading(true);
    wizardContext.projectionProviderService.getBulkTargetingProjectsAsync().then((response) => {
      const tabledata: BulkProviderChangeProjectTableData[] = response.map((project) => {
        const detailsArr: string[] = [];
        if (project.isReadOnly) {
          detailsArr.push("Read Only");
        }
        if (project.hasExclusions) {
          detailsArr.push("Existing Excluded Providers");
        }
        if (project.hasTargets) {
          detailsArr.push("Existing Targeted Providers");
        }
        if (!project.hasMarketOrProspect) {
          detailsArr.push("No Market/Prospect Network");
        }
        const detailsStr = detailsArr.join(" And ");
        return {
          /* eslint-disable sort-keys */
          rowIsDisabled: project.isReadOnly || !project.hasMarketOrProspect,
          key: project.projectId.toString(),
          projectId: project.projectId,
          projectName: project.projectName,
          templateName: project.templateName,
          details: detailsStr,
          /* eslint-enable sort-keys */
        };
      });
      wizardContext.setProjects(tabledata);
      setIsLoading(false);
    });
  }, []);

  const renderedProjects = useMemo(() => {
    if (!projectSearchString) {
      return wizardContext.projects.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    }
    const regex = new RegExp(projectSearchString, "i");
    const filteredProjectList = wizardContext.projects.filter((project) => !!project.projectName.match(regex));
    return filteredProjectList.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
  }, [projectSearchString, wizardContext.projects]);

  const handleCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    if (isBulk) {
      let newSelectedProjectIds = [...wizardContext.selectedProjectIds];
      if (selectedKeys.length > 0) {
        newSelectedProjectIds.push(...selectedKeys);
      } else {
        const removedIds = renderedProjects.map((project) => project.projectId.toString());
        newSelectedProjectIds = newSelectedProjectIds.filter((id) => { return !removedIds.includes(id); });
      }
      wizardContext.setSelectedProjectIds([...new Set(newSelectedProjectIds)]);
    } else {
      wizardContext.setSelectedProjectIds(selectedKeys);
    }
  };

  useEffect(() => {
    props.stepIsValid(wizardContext.selectedProjectIds.length > 0);
  }, [wizardContext.selectedProjectIds]);

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="copy-text-default">Select the projects you would like to target from.</p>
        <QESAlert type="info" classNames="mb-2" persist={true}>
          Any project that has current targets or excludes will be overwritten if selected. Readonly projects can not be selected.
        </QESAlert>

        <SearchBox
          escapeSearchValue={true}
          fieldsetClass="d-flex"
          inputLabel="Search Projects"
          onSubmit={(value) => setProjectSearchString(value)}
          testId="import-targeting-project-search"
        />
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <Waffles<BulkProviderChangeProjectTableData>
              columns={ProjectsTableColumnConfig}
              data={renderedProjects}
              fullWidth={true}
              loading={isLoading}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={wizardContext.selectedProjectIds}
              showCheckboxes={true}
              testId="import-targeting-projects-table"
              totalNumberOfRecords={wizardContext.projects.length}
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default BulkTargetingWizardSelectProjects;
